"use client";
/**
 * ConversationNavPanel — floating right-edge overview of the conversation.
 *
 * Renders one dot per user question plus up/down chevrons. Clicking a dot
 * scrolls the corresponding question into view. The chevrons scroll the
 * conversation container to the very top or bottom.
 */
import { useEffect, useState, useCallback } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

interface ConversationNavPanelProps {
  /** Refs to each user-question DOM node, in order */
  questionRefs: React.RefObject<HTMLElement | null>[];
  /** The scrollable container that holds the messages */
  scrollContainerRef: React.RefObject<HTMLElement | null>;
}

const chevronBtnStyle: React.CSSProperties = {
  display:        "flex",
  alignItems:     "center",
  justifyContent: "center",
  width:          22,
  height:         22,
  borderRadius:   "50%",
  border:         "1px solid rgba(100,116,139,0.25)",
  background:     "transparent",
  cursor:         "pointer",
  color:          "rgba(100,116,139,0.6)",
  padding:        0,
  transition:     "all 0.2s",
  flexShrink:     0,
};

export function ConversationNavPanel({
  questionRefs,
  scrollContainerRef,
}: ConversationNavPanelProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const updateActive = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const containerTop = container.getBoundingClientRect().top;
    let closestIndex = 0;
    let closestDist = Infinity;

    questionRefs.forEach((ref, i) => {
      const el = ref.current;
      if (!el) return;
      const dist = Math.abs(el.getBoundingClientRect().top - containerTop);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });

    setActiveIndex((prev) => (prev === closestIndex ? prev : closestIndex));
  }, [questionRefs, scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", updateActive, { passive: true });
    updateActive();
    return () => container.removeEventListener("scroll", updateActive);
  }, [updateActive, scrollContainerRef]);

  const handleDotClick = (index: number) => {
    const el = questionRefs[index]?.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToTop = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToBottom = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  };

  if (questionRefs.length === 0) return null;

  const count = questionRefs.length;
  const dotsHeight = Math.min(count * 20, 300);

  return (
    <div
      className="fixed right-3 z-40"
      style={{
        top:           "50%",
        transform:     "translateY(-50%)",
        width:         "22px",
        display:       "flex",
        flexDirection: "column",
        alignItems:    "center",
        gap:           8,
      }}
      role="navigation"
      aria-label="Conversation overview"
    >
      {/* Scroll to top */}
      <button onClick={scrollToTop} title="Scroll to top" aria-label="Scroll to top" style={{ ...chevronBtnStyle, marginBottom: 6 }}>
        <ChevronUp size={13} />
      </button>

      {/* Dots */}
      <div style={{ position: "relative", width: 16, height: dotsHeight, flexShrink: 0 }}>
        {questionRefs.map((_, i) => {
          const pct      = count === 1 ? 50 : (i / (count - 1)) * 100;
          const isActive = i === activeIndex;
          return (
            <button
              key={i}
              onClick={() => handleDotClick(i)}
              title={`Question ${i + 1}`}
              aria-label={`Scroll to question ${i + 1}`}
              style={{
                position:   "absolute",
                left:       "50%",
                top:        `${pct}%`,
                transform:  "translate(-50%, -50%)",
                width:      isActive ? 10 : 8,
                height:     isActive ? 10 : 8,
                padding:    0,
                border:     "none",
                background: "transparent",
                cursor:     "pointer",
              }}
            >
              <span
                style={{
                  display:      "block",
                  width:        isActive ? 10 : 8,
                  height:       isActive ? 10 : 8,
                  borderRadius: "50%",
                  background:   isActive ? "#6366f1" : "rgba(100,116,139,0.35)",
                  transition:   "all 0.2s",
                }}
              />
            </button>
          );
        })}
      </div>

      {/* Scroll to bottom */}
      <button onClick={scrollToBottom} title="Scroll to bottom" aria-label="Scroll to bottom" style={{ ...chevronBtnStyle, marginTop: 6 }}>
        <ChevronDown size={13} />
      </button>
    </div>
  );
}
